import { useInfiniteQuery, QueryFunction } from '@tanstack/react-query';
import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import Select, { GroupBase } from 'react-select';
import { maskSelectCpfCnpj } from 'utils/formatCNPJOrCPF';
import styles from './selectInfiniteScroll.module.scss';
import { BrButton, BrLoading } from '@govbr-ds/react-components';
import { IRepresentante } from 'interfaces/IRepresentante';

const CustomLoadingIndicator = () => {
  return (
    <div className={styles.loadingContainer}>
      <BrLoading />
    </div>
  );
};

export interface ISelectInfiniteScrollProps {
  label?: string;
  value: {
    label: string;
    value: string | number;
  } | null;

  onChange: Function;
  functionQuery: QueryFunction<any, any[], number>;
  keyQuery: any[];
  queryBuscaInput?: string;
  setQueryBuscaInput?: Dispatch<SetStateAction<string>>;
  formatResult?: Function;
  placeholder?: string;
  errorMessage?: string;
  id?: string;
  isDisabled?: boolean;
  customClassName?: string;
  enableQuery?: boolean;
  isPlaceHolderMunicipio?: boolean;
  setLabel?: Function;
  defaultValue?: string;
  setRetornoQuery?: Dispatch<SetStateAction<any[]>>;
  hasIcon?: boolean;
  hasBorder?: boolean;
}

const SelectInfiniteScroll = ({
  label,
  value,
  onChange,
  functionQuery,
  keyQuery,
  queryBuscaInput = '',
  setQueryBuscaInput = () => {},
  formatResult,
  placeholder = 'Selecionar',
  errorMessage = 'Não foi possível carregar os dados',
  id,
  isDisabled = false,
  customClassName = '',
  enableQuery = true,
  hasIcon = false,
  hasBorder = true,
}: ISelectInfiniteScrollProps) => {
  const [options, setOptions] = useState<
    ({ label: string; value: string | number } | GroupBase<{ label: string; value: string | number }>)[]
  >([]);

  const { data, error, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, refetch } = useInfiniteQuery({
    queryKey: keyQuery,
    queryFn: functionQuery,
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    enabled: enableQuery,
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.page.number;
      const totalPages = lastPage.page.totalPages;

      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
  });

  useEffect(() => {
    if ((isFetching || isFetchingNextPage) && !!queryBuscaInput) {
      setOptions([]);
    } else if (data) {
      let newOptions: (
        | { label: string; value: string | number }
        | GroupBase<{ label: string; value: string | number }>
      )[];

      if (formatResult) {
        newOptions = formatResult(data);
      } else {
        newOptions = data.pages.flatMap((page) =>
          page.content.map((item: IRepresentante) => ({
            label: maskSelectCpfCnpj(item.id.toString()),
            value: item.id,
          }))
        );
      }

      setOptions(newOptions);
    }
  }, [data, isFetching, isFetchingNextPage]);

  const customStyles = {
    control: (base: any, state: any) => ({
      ...base,
      cursor: 'text',
      border: hasBorder ? '1px solid #ccc' : 'none',
      fontSize: '16px',
      fontWeight: '500',
      lineHeight: '23.21px',
    }),
    option: (base: any, state: any) => ({
      ...base,
      cursor: 'pointer',
      backgroundColor: state.isSelected ? '#2670e8' : 'white',
      '&:hover': {
        backgroundColor: state.isSelected ? '#4785e9' : '#e4e4e4',
      },
      borderBottom: '1px solid #ccc',
      padding: '15px 10px',
      '&:last-child': {
        borderBottom: 'none',
      },
      fontWeight: '500',
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      cursor: 'pointer',
      color: 'blue',
    }),
  };

  return (
    <>
      <div className={styles.label}>{label}</div>

      <div className={styles.selectContainer}>
        {error == null && (
          <Select
            id={id}
            className={`${styles.select} ${customClassName} ${hasIcon ? styles.selectWithIcon : ''}`}
            value={value}
            onChange={(newValue) => onChange(newValue || null)}
            options={options}
            onMenuScrollToBottom={hasNextPage ? () => fetchNextPage() : undefined}
            styles={customStyles}
            isLoading={isFetching || isFetchingNextPage}
            isSearchable={queryBuscaInput !== undefined}
            onInputChange={(value: string) => {
              setQueryBuscaInput(value);
            }}
            loadingMessage={() => <>Carregando...</>}
            inputValue={queryBuscaInput}
            filterOption={null}
            placeholder={placeholder}
            defaultValue={value}
            components={{ LoadingIndicator: CustomLoadingIndicator }}
            isDisabled={isDisabled}
          />
        )}
        {error && (
          <div className={styles.errorContainer}>
            <span className={styles.errorText}>{errorMessage}</span>
            <BrButton size='small' onClick={() => refetch()}>
              (Tentar novamente)
            </BrButton>
          </div>
        )}
      </div>
    </>
  );
};

export default SelectInfiniteScroll;
